
// Vue
import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator";
import { namespace } from "vuex-class";

// Third party
import { validationMixin } from "vuelidate";
import { required, minLength, maxLength, requiredIf, url } from "vuelidate/lib/validators";

// Components
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import vSelect from "vselect3";

// Types
import { Region } from "@/store/types";

// Default Region
import { regionTemplate } from "@/components/settings/regions/RegionSettings.vue";

// Namespaces
const Regions = namespace("regions");

@Component({
    mixins: [validationMixin],
    validations: {
        currentRegion: {
            title: {
                required,
                minLength: minLength(1),
                maxLength: maxLength(250)
            },
            url: {
                required,
                url,
                minLength: minLength(1),
                maxLength: maxLength(250),
            }
        },
        hasDataChanged: {
            required: requiredIf(function() {
                return (
                    this.selectedRegion.title === this.currentRegion.title &&
                    this.selectedRegion.url === this.currentRegion.url &&
                    this.selectedRegion.friendlyName === this.currentRegion.friendlyName &&
                    this.selectedRegion.active === this.currentRegion.active
                );
            })
        }
    },
    components: {
        "v-select": vSelect,
        VuePerfectScrollbar: VuePerfectScrollbar
    }
})

export default class RegionEditModal extends Vue {
    // Props
    @Prop({ default: null, type: Object }) selectedRegion: Region;
    @Prop({ default: false, type: Boolean }) visible: boolean;

    // Actions
    @Regions.Action createRegion: (region: Region) => Promise<void>;
    @Regions.Action updateRegion: (region: Region) => Promise<void>;

    private currentRegion: Region = { ...regionTemplate };

    @Emit("toggle-modal")
    private closeModal(): void {
        // Reset data back to default state
        this.currentRegion = { ...regionTemplate };
    }

    @Watch("visible")
    private modalToggle(toggle: boolean): void {
        if (toggle) {
            this.currentRegion = { ...this.selectedRegion };
        } else {
            this.currentRegion = { ...regionTemplate };
        }
    }

    // When the region Id is 0, assume that we are creating the region. Otherwise update the region.
    private get isCreate(): boolean {
        return this.currentRegion.regionId == 0;
    }
    
    // This function saves the region onto the database
    private async saveRegion(): Promise<void> {
        if (this.isCreate) {
            await this.createRegion(this.currentRegion);
        } else {
            await this.updateRegion(this.currentRegion);
        }
        
        this.closeModal();
    }
}
